import {
  AIRTIME_MANAGEMENT,
  CARRIER_MANAGEMENT,
  FILTER_FEATURE,
  PARTNER_MANAGEMENT,
  STORE_API,
} from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { PackageRegistrationResponseModel } from "features/packageRegistration/data/model/PackageRegistrationResponseModel";
import { PackageRegistrationInjection } from "features/packageRegistration/di/PackageRegistrationInjection";
import { ExportPayload } from "features/packageRegistration/domain/entities/PackageRegistrationPayload";
import {
  PackageRegistrationQuery,
  SearchContext,
  SearchFormValues,
} from "features/packageRegistration/domain/entities/PackageRegistrationQuery";
import { PackageRegistrationRequestEntity } from "features/packageRegistration/domain/entities/PackageRegistrationRequestEntity";
import { IPackageRegistrationRepository } from "features/packageRegistration/domain/repositories/IPackageRegistrationRepository";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { DateFormat } from "utils/format/DateFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";

@injectable()
export class PackageRegistrationUseCase {
  private packageRegistrationRepo: IPackageRegistrationRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.packageRegistrationRepo =
      PackageRegistrationInjection.getPackageRegistrationRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  public shouldUsePrivateTransactionList(): boolean {
    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities
    );
    const hasPrivateTransactionList =
      Array.isArray(cachedCapabilities) &&
      cachedCapabilities.some(
        (item) =>
          (typeof item === "string" ? item : item?.key) ===
          CapabilityEnum.privateTransList
      );

    return hasPrivateTransactionList === true;
  }

  public shouldUsePrivateTransactionDetail(): boolean {
    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities
    );
    const hasPrivateTransactionDetail =
      Array.isArray(cachedCapabilities) &&
      cachedCapabilities.some(
        (item) =>
          (typeof item === "string" ? item : item?.key) ===
          CapabilityEnum.privateTransDetail
      );

    return hasPrivateTransactionDetail === true;
  }

  private getListFromRepository(params: PackageRegistrationRequestEntity) {
    return this.packageRegistrationRepo.getList(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  private prepareFormValues(
    rawFormValues: Record<string, unknown>
  ): SearchFormValues {
    return {
      status: paramsChecking(rawFormValues?.status, "select") as
        | number
        | string,
      code: paramsChecking(rawFormValues?.code, "input") as string,
      transRef: paramsChecking(rawFormValues?.transRef, "input") as string,
      type: paramsChecking(rawFormValues?.type, "select") as number | string,
      platform: paramsChecking(rawFormValues?.platform, "select") as string,
      partner: paramsChecking(rawFormValues?.partner, "select") as string,
      user: (rawFormValues?.user as string[]) || undefined,
      store: paramsChecking(rawFormValues?.store, "select") as string,
    };
  }

  private getDateRange(rawFormValues: Record<string, unknown>): {
    dateFrom: string;
    dateTo: string;
  } {
    const dateFrom =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "from"
      ) ?? "";
    const dateTo =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "to"
      ) ?? "";
    return { dateFrom, dateTo };
  }

  async getList(
    params: PackageRegistrationRequestEntity
  ): Promise<ListBaseApiResponseModel<PackageRegistrationResponseModel>> {
    return await this.getListFromRepository(params);
  }

  async getPackageRegistrationListFromSearch(
    rawFormValues: Record<string, unknown>,
    context: SearchContext
  ): Promise<{
    list: ListBaseApiResponseModel<PackageRegistrationResponseModel>;
    params: PackageRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = PackageRegistrationQuery.buildSearchQuery(
      formValues,
      context,
      dateFrom,
      dateTo
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getPackageRegistrationListWithTableChange(
    prevParams: PackageRegistrationRequestEntity,
    rawFormValues: Record<string, unknown>,
    page: number,
    limit: number
  ): Promise<{
    list: ListBaseApiResponseModel<PackageRegistrationResponseModel>;
    params: PackageRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = PackageRegistrationQuery.buildTableChangeQuery(
      prevParams,
      formValues,
      page,
      limit,
      dateFrom,
      dateTo
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getPackageRegistrationListRedo(): Promise<{
    list: ListBaseApiResponseModel<PackageRegistrationResponseModel>;
    params: PackageRegistrationRequestEntity;
  }> {
    const { fromDate, toDate } = DateFormat.getDateRange(0);
    const params = PackageRegistrationQuery.buildRedoQuery(
      fromDate ?? "",
      toDate ?? ""
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getDetail(
    params: PackageRegistrationRequestEntity
  ): Promise<BaseApiResponseModel<PackageRegistrationResponseModel>> {
    return await this.packageRegistrationRepo.getDetail(
      params,
      this.shouldUsePrivateTransactionDetail()
    );
  }

  async exportReport(
    params: PackageRegistrationRequestEntity
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.packageRegistrationRepo.exportReport(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  async exportPackageRegistrationReportFromSearch(
    payload: ExportPayload
  ): Promise<BaseApiResponseModel<Blob>> {
    const formValues = this.prepareFormValues(payload.rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(payload.rawFormValues);
    const params = PackageRegistrationQuery.buildExportQuery(
      formValues,
      payload.context,
      dateFrom,
      dateTo
    );
    return await this.exportReport(params);
  }

  async checkStatus(
    code: string
  ): Promise<BaseApiResponseModel<PackageRegistrationResponseModel>> {
    return await this.packageRegistrationRepo.checkStatus(code);
  }

  async getFilterList(
    params: BaseFilterListRequestModel,
    target: "carrier" | "partner" | "store" | "denomination" | "package"
  ): Promise<Array<BaseFilterListResponseModel>> {
    let path = "";
    switch (target) {
      case "carrier":
        path = CARRIER_MANAGEMENT.LIST;
        break;
      case "partner":
        path = PARTNER_MANAGEMENT.LIST;
        break;
      case "store":
        path = STORE_API.LIST;
        break;
      case "denomination":
        path = AIRTIME_MANAGEMENT.LIST;
        break;
      case "package":
        path = FILTER_FEATURE.PACKAGE_LIST;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
    });
  }

  async getUserForFilter(
    params: BaseUserRequestModel,
    target: "staff" | "userB2B"
  ): Promise<Array<BaseUserResponseModel>> {
    let path = "";
    switch (target) {
      case "staff":
        path = FILTER_FEATURE.STAFF_LIST;
        break;
      case "userB2B":
        path = FILTER_FEATURE.USER_LIST_B2B;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
    });
  }
}
